import { Injectable } from '@angular/core';
import { map, switchMap } from 'rxjs/operators';
import { from, Observable } from 'rxjs';

import { ApiService } from './api.service';

import { NextPanel } from 'src/app/models/next-panel';
import { CheckoutDocuments, CheckoutDependents, CheckoutDependentsPayload, CheckoutMemberSSN, CheckoutSurcharge, CheckoutSurchargePayload, CheckoutSpousalExclusion, CheckoutExclusionPayload, WizardCoverageSummary, CheckoutFinalize, CheckoutFinalizePayload, CheckoutWaiveDocument, CheckoutAdminEvent, CheckoutComplete, CheckoutVerificationCode } from 'src/app/models/enrollment-checkout';
import { USAePay, USAePayPayload } from 'src/app/models/USAePay';
import { BeneficiariesResponse, BeneficiaryPayload } from 'src/app/models/beneficiary';
import { BenefitSummaryList } from 'src/app/models/benefit-summary';
import { OTPResponse } from '../models/auth';
import { EncryptionService } from './encryption.service';
import { PassEncryption } from '@app/models/encryption';

const controllerRoute = '/checkout';

@Injectable()
export class EnrollmentCheckoutService {

    constructor(
        private apiService: ApiService,
        private encryptionService: EncryptionService,

    ) {
    }

    public initializeSerivce(): Observable<NextPanel> {
        return this.apiService.get(`${controllerRoute}/initialize`).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getDocuments(): Observable<CheckoutDocuments> {
        return this.apiService.get(`${controllerRoute}/documents`).pipe(
            map((response: CheckoutDocuments) => {
                return response;
            }),
        );
    }

    /**
     * No Payload. Documents are submitted by another call.
     * @returns 
     */
    public submitDocuments(): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/documents`, null).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getPCNADependent(): Observable<CheckoutDependents> {
        return this.apiService.get(`${controllerRoute}/pcna-dependent`).pipe(
            map((response: CheckoutDependents) => {
                return response;
            }),
        );
    }

    public submitPCNADependent(): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/pcna-dependent`, null).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getUSAePay(): Observable<USAePay> {
        return this.apiService.get(`${controllerRoute}/usaepay`).pipe(
            map((response: USAePay) => {
                return response;
            }),
        );
    }

    public submitUSAePay(payload: USAePayPayload): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/usaepay`, payload).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getDependentSSN(): Observable<CheckoutDependents> {
        return this.apiService.get(`${controllerRoute}/dependent-ssn`).pipe(
            map((response: CheckoutDependents) => {
                return response;
            }),
        );
    }

    public submitDependentSSN(payload: CheckoutDependentsPayload): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/dependent-ssn`, payload).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    /**
     * @param payload CheckoutMemberSSN
     * @returns No Content
     */
    public submitMemberSSN(payload: CheckoutMemberSSN): Observable<null> {
        return this.apiService.post(`${controllerRoute}/member-ssn`, payload).pipe(
            map((response: null) => {
                return response;
            }),
        );
    }

    public getDependentVerify(): Observable<CheckoutDependents> {
        return this.apiService.get(`${controllerRoute}/dependent-verify`).pipe(
            map((response: CheckoutDependents) => {
                return response;
            }),
        );
    }

    /**
     * No Payload as the logic does not allow for changing dependents info.
     */
    public submitDependentVerify(): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/dependent-verify`, null).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getSmokerSurcharge(): Observable<CheckoutSurcharge> {
        return this.apiService.get(`${controllerRoute}/smoker-surcharge`).pipe(
            map((response: CheckoutSurcharge) => {
                return response;
            }),
        );
    }

    public submitSmokerSurcharge(payload: CheckoutSurchargePayload): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/smoker-surcharge`, payload).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getSpousalSurcharge(): Observable<CheckoutSurcharge> {
        return this.apiService.get(`${controllerRoute}/spousal-surcharge`).pipe(
            map((response: CheckoutSurcharge) => {
                return response;
            }),
        );
    }

    public submitSpousalSurcharge(payload: CheckoutSurchargePayload): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/spousal-surcharge`, payload).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getSpousalExclusion(): Observable<CheckoutSpousalExclusion> {
        return this.apiService.get(`${controllerRoute}/spousal-exclusion`).pipe(
            map((response: CheckoutSpousalExclusion) => {
                return response;
            }),
        );
    }

    public submitSpousalExclusion(payload: CheckoutExclusionPayload): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/spousal-exclusion`, payload).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getAutoEnroll(): Observable<WizardCoverageSummary> {
        return this.apiService.get(`${controllerRoute}/auto-enroll`).pipe(
            map((response: WizardCoverageSummary) => {
                return response;
            }),
        );
    }

    public submitAutoEnroll(): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/auto-enroll`, null).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getWaiveDocuments(): Observable<CheckoutWaiveDocument> {
        return this.apiService.get(`${controllerRoute}/waive-documents`).pipe(
            map((response: CheckoutWaiveDocument) => {
                return response;
            }),
        );
    }

    public getBeneficiaries(): Observable<BeneficiariesResponse> {
        return this.apiService.get(`${controllerRoute}/beneficiaries`).pipe(
            map((response: BeneficiariesResponse) => {
                return response;
            }),
        );
    }

    public submitBeneficiaries(payload: BeneficiaryPayload): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/beneficiaries`, payload).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getAdminEventNotice(): Observable<CheckoutAdminEvent> {
        return this.apiService.get(`${controllerRoute}/admin-event`).pipe(
            map((response: CheckoutAdminEvent) => {
                return response;
            }),
        );
    }

    public submitAdminEventNotice(): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/admin-event`, null).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getSummary(): Observable<BenefitSummaryList> {
        return this.apiService.get(`${controllerRoute}/summary`).pipe(
            map((response: BenefitSummaryList) => {
                return response;
            }),
        );
    }

    public submitSummary(): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/summary`, null).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getFinalize(): Observable<CheckoutFinalize> {
        return this.apiService.get(`${controllerRoute}/finalize`).pipe(
            map((response: CheckoutFinalize) => {
                return response;
            }),
        );
    }

    public submitFinalizeOtpCode(payload: CheckoutFinalizePayload): Observable<OTPResponse> {
        const postFinalize = () =>
            this.apiService
                .post(`${controllerRoute}/finalize-otp-code`, payload)
                .pipe(map((response: OTPResponse) => response));

        if (!payload.agentPassword) {
            return postFinalize();
        }

        return from(this.encryptionService.encryptPassword(payload.agentPassword)).pipe(
            switchMap((encryptedPassword: PassEncryption) => {
                payload.agentPassword = null;
                payload.passEncryption.a1 = encryptedPassword.a1;
                payload.passEncryption.m1 = encryptedPassword.m1;
                payload.passEncryption.m2 = encryptedPassword.m2;
                return postFinalize();
            }),
        );
    }

    /**
     * API will throw an error if failed and No Content if passed.
     * @returns No Content
     */
    public submitFinalizeOtpVerification(payload: CheckoutVerificationCode): Observable<boolean> {
        return this.apiService.post(`${controllerRoute}/finalize-otp-code/verification`, payload).pipe(
            map((response: any) => {
                return true;
            }),
        );
    }

    /**
     * All other screens should be done before this call.
     * @returns NextPanel
     */
    public submitFinalize(): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/finalize`, null).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getComplete(): Observable<CheckoutComplete> {
        return this.apiService.get(`${controllerRoute}/complete`).pipe(
            map((response: CheckoutComplete) => {
                return response;
            }),
        );
    }
}